import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import Button from '~/modules/components/global/button';
import Icon from '~/modules/components/global/icon';
import EmptyState from '~/modules/components/global/empty-state';
import { Input } from '~/components/ui/input';
import {
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '~/components/ui/select';
import {
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '~/components/ui/table';
import { Label } from '~/components/ui/label';
import confirm from '~/modules/components/global/confirm';
import ToastService from '~/modules/services/toast-service';
import ServiceAccountService from '~/modules/services/service-account-service';
import useAuth from '~/modules/hooks/use-auth';
import ServiceAccountTokens from './service-account-tokens';
import { captureError } from '../../../pixwel/exception-handler.factory';

const QUERY_KEY = ['serviceAccounts'];

/**
 * The groups whose service accounts this person may manage, as `{ label, value }` options.
 *
 * Read straight off the session rather than derived from permissions: the API computes it in
 * `Permissions::serviceAccountGroups()` and sends it as `serviceAccountGroups`, so there is one rule
 * in one place. The API re-checks every request regardless, so a stale session yields a 403 rather
 * than unauthorised access.
 */
export const manageableGroups = (session) => (session?.serviceAccountGroups || [])
	.map((group) => ({
		label: group?.name || String(group?._id || group?.id),
		value: String(group?._id || group?.id)
	}))
	.filter((option) => option.value && option.value !== 'undefined');

const ServiceAccounts = () => {
	const queryClient = useQueryClient();
	const { session } = useAuth();

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [group, setGroup] = useState(null);
	const [expandedId, setExpandedId] = useState(null);

	const groupOptions = useMemo(() => manageableGroups(session), [session]);

	// With a single manageable group there is nothing to choose, so don't make anyone choose it.
	const selectedGroup = group || (groupOptions.length === 1 ? groupOptions[0].value : null);

	const { data: accounts = [], isLoading } = useQuery(
		QUERY_KEY,
		() => ServiceAccountService.list(),
		{ refetchOnWindowFocus: false }
	);

	const { mutateAsync: createAccount, isLoading: isCreating } = useMutation(
		(payload) => ServiceAccountService.create(payload),
		{
			onSuccess: (account) => {
				queryClient.invalidateQueries(QUERY_KEY);
				setName('');
				setDescription('');
				setGroup(null);
				ToastService.create('success', `Service account "${account.name}" created`);
			},
			onError: (err) => {
				const message = err?.response?.data?.error || err.message || 'Failed to create service account';
				ToastService.create('error', message);
				captureError(err, `Error creating service account: ${err.message}`);
			}
		}
	);

	const { mutateAsync: setDisabled } = useMutation(
		({ id, disabled }) => (
			disabled
				? ServiceAccountService.disable(id)
				: ServiceAccountService.update(id, { disabled: false })
		),
		{
			onSuccess: (_result, { id, disabled }) => {
				queryClient.invalidateQueries(QUERY_KEY);

				// Disabling revokes every token server-side. If this row is expanded its token list is
				// still mounted, and window-focus refetching is off, so without this it would keep
				// presenting revoked tokens as active — including after a re-enable, which does not
				// bring them back.
				queryClient.invalidateQueries(['serviceAccountTokens', id]);

				ToastService.create('success', disabled ? 'Service account disabled' : 'Service account enabled');
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to update service account');
				captureError(err, `Error updating service account: ${err.message}`);
			}
		}
	);

	const handleCreate = async() => {
		const trimmed = name.trim();

		if( !trimmed || !selectedGroup ) {
			return;
		}

		await createAccount({ name: trimmed, group: selectedGroup, description: description.trim() });
	};

	const handleDisable = async(row) => {
		const ok = await confirm({
			title: 'Disable service account',
			content: `Disable "${row.name}"? Its tokens are revoked immediately and every integration `
				+ 'using them stops working. Re-enabling does not restore them.',
			confirmText: 'Disable',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});

		if( !ok ) {
			return;
		}

		await setDisabled({ id: row._id, disabled: true });
	};

	const groupName = (id) => {
		const match = groupOptions.find((option) => option.value === String(id));
		return match ? match.label : id;
	};

	const toggleExpanded = (id) => {
		setExpandedId((prev) => (prev === id ? null : id));
	};

	return (
		<div data-testid="preferences-service-accounts">
			<div className="mb-8">
				<h3 className="text-base font-semibold mb-1">Create service account</h3>
				<p className="text-sm text-muted-foreground mb-4">
					Service accounts belong to a group, not to you. They keep working when people join or
					leave, and their access is whatever the owning group has been granted. Use them for
					integrations; use API tokens for your own scripts.
				</p>
				<div className="flex gap-3 items-end flex-wrap">
					<div className="flex flex-col gap-1.5 flex-1 min-w-[14rem]">
						<Label htmlFor="service-account-name">Name</Label>
						<Input
							id="service-account-name"
							placeholder="e.g. Nightly Sync"
							value={name}
							onChange={(e) => setName(e.target.value)}
							data-testid="service-account-name-input"
						/>
					</div>
					{groupOptions.length > 1 && (
						<div className="flex flex-col gap-1.5 w-56">
							<Label>Group</Label>
							<Select value={selectedGroup ?? undefined} onValueChange={setGroup}>
								<SelectTrigger data-testid="service-account-group-select">
									<SelectValue placeholder="Select a group" />
								</SelectTrigger>
								<SelectContent>
									{groupOptions.map((opt) => (
										<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}
					<div className="flex flex-col gap-1.5 flex-1 min-w-[14rem]">
						<Label htmlFor="service-account-description">Purpose</Label>
						<Input
							id="service-account-description"
							placeholder="What will use this?"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							data-testid="service-account-description-input"
						/>
					</div>
					<Button
						onClick={handleCreate}
						loading={isCreating}
						disabled={!name.trim() || !selectedGroup || isCreating}
						data-testid="service-account-create-button"
					>
						Create service account
					</Button>
				</div>
			</div>

			<div>
				<h3 className="text-base font-semibold mb-3">Service accounts</h3>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead></TableHead>
							<TableHead>Name</TableHead>
							<TableHead>Group</TableHead>
							<TableHead>Purpose</TableHead>
							<TableHead>Status</TableHead>
							<TableHead></TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading
							? (
								<TableRow>
									<TableCell colSpan={6} className="text-center text-muted-foreground">Loading…</TableCell>
								</TableRow>
							)
							: accounts.length === 0
								? (
									<TableRow>
										<TableCell colSpan={6}>
											<EmptyState message="No service accounts" />
										</TableCell>
									</TableRow>
								)
								: (
									accounts.map((account) => (
										<React.Fragment key={account._id}>
											<TableRow className={account.disabledAt ? 'opacity-60' : ''}>
												<TableCell className="w-8">
													<button
														type="button"
														onClick={() => toggleExpanded(account._id)}
														aria-label="Toggle tokens"
														className="text-muted-foreground hover:text-foreground"
													>
														{expandedId === account._id
															? <ChevronDown className="h-4 w-4" />
															: <ChevronRight className="h-4 w-4" />}
													</button>
												</TableCell>
												<TableCell className="font-medium max-w-[10rem] truncate">{account.name}</TableCell>
												<TableCell>{groupName(account.group)}</TableCell>
												<TableCell className="max-w-[14rem] truncate">{account.description}</TableCell>
												<TableCell>{account.disabledAt ? 'Disabled' : 'Active'}</TableCell>
												<TableCell className="text-right">
													{account.disabledAt
														? (
															<Button
																variant="secondary"
																data-testid="service-account-enable-button"
																onClick={() => setDisabled({
																	id: account._id, disabled: false
																})}
															>
																Enable
															</Button>
														)
														: (
															<Button
																variant="danger"
																data-testid="service-account-disable-button"
																onClick={() => handleDisable(account)}
															>
																<Icon use="trash" color="white" /> Disable
															</Button>
														)}
												</TableCell>
											</TableRow>
											{expandedId === account._id && (
												<TableRow>
													<TableCell colSpan={6} className="bg-muted/30">
														<ServiceAccountTokens account={account} />
													</TableCell>
												</TableRow>
											)}
										</React.Fragment>
									))
								)}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};

export default ServiceAccounts;
